"use client";

import { useEffect, useRef, useState } from "react";
import { trackSiteGlueEvent } from "~/lib/siteglue/cta-config";
import { sanitizeHireConversionContinueUrl } from "~/lib/siteglue/hire-conversion";

const FIRED_KEY = "sg_agent_hired_conversion_fired";

type Props = {
  continueUrl: string | null;
  hired: boolean;
};

/**
 * Fires Google Ads / GA4 hire conversion on siteglue.ai/welcome, then continues to app.
 * Smart Campaigns only accept same-domain confirmation URLs (not app.easygpt.builders).
 */
export function HireWelcomeConversion({ continueUrl, hired }: Props) {
  const fired = useRef(false);
  const [status, setStatus] = useState<"tracking" | "redirecting" | "ready">(
    continueUrl ? "tracking" : "ready",
  );

  useEffect(() => {
    if (!hired || fired.current || typeof window === "undefined") return;

    const safeContinue = sanitizeHireConversionContinueUrl(continueUrl);
    const already = sessionStorage.getItem(FIRED_KEY) === "1";

    if (!already) {
      fired.current = true;
      sessionStorage.setItem(FIRED_KEY, "1");
      trackSiteGlueEvent("agent_hired", { conversion: true });
      trackSiteGlueEvent("sign_up_complete", { conversion: true, source: "hire_welcome" });
    }

    if (!safeContinue) {
      setStatus("ready");
      return;
    }

    setStatus("redirecting");
    const t = window.setTimeout(() => {
      window.location.replace(safeContinue);
    }, 1200);
    return () => window.clearTimeout(t);
  }, [continueUrl, hired]);

  return (
    <p className="mt-4 text-sm text-slate-500">
      {status === "redirecting"
        ? "Taking you to your AI Employee settings…"
        : status === "tracking"
          ? "Confirming your hire…"
          : "Open your dashboard to finish setup."}
    </p>
  );
}
